import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Plus, Trash2, Coins, Package } from "lucide-react";
import { api, formatApiError, fmtGB, fmtEUR } from "../lib/api";
import { GlassCard, PageHeader, EmptyState, Spinner } from "../components/common";
import { TutorialHint } from "../components/TutorialHint";
import { useAuth } from "../context/AuthContext";

const ROLE_LABEL = { super_admin: "Admin", reseller: "Revenda" };

export default function Reseller() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState(null);
  const [newPlan, setNewPlan] = useState({ name: "", price_eur: "", credits_gb: "", max_clients: "" });
  const [newClient, setNewClient] = useState({ name: "", email: "", password: "", credits_gb: 50 });
  const [creditInputs, setCreditInputs] = useState({});

  const load = () => {
    api.get("/saas/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/saas/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => { load(); }, []);

  const createPlan = async () => {
    try {
      await api.post("/saas/plans", { name: newPlan.name, price_eur: parseFloat(newPlan.price_eur), credits_gb: parseInt(newPlan.credits_gb), max_clients: parseInt(newPlan.max_clients) });
      toast.success("Plano criado");
      setNewPlan({ name: "", price_eur: "", credits_gb: "", max_clients: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deletePlan = async (id) => {
    try {
      await api.delete(`/saas/plans/${id}`);
      toast.success("Plano removido");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const createClient = async () => {
    try {
      await api.post("/saas/users", { ...newClient, credits_gb: parseFloat(newClient.credits_gb) || 0 });
      toast.success("Conta de revenda criada com sucesso");
      setNewClient({ name: "", email: "", password: "", credits_gb: 50 });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addCredits = async (userId) => {
    const gb = parseFloat(creditInputs[userId]);
    if (!gb) return;
    try {
      await api.post("/saas/credits", { user_id: userId, gb, reason: "Recarga manual" });
      toast.success(`${gb > 0 ? "+" : ""}${gb} GB aplicados`);
      setCreditInputs({ ...creditInputs, [userId]: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="reseller-page">
      <PageHeader eyebrow="SaaS" title="Revendedores" subtitle="Gerencie planos, crie contas de revenda e distribua créditos de streaming." />

      <TutorialHint
        id="reseller"
        title="Como funciona a revenda"
        steps={[
          "O admin define os planos (preço, GB inclusos, limite de revendas).",
          "Crie contas de revendedor com saldo inicial de créditos.",
          "Ajuste o saldo de qualquer revendedor a qualquer momento (+ ou − GB).",
          "Revendedores recarregam sozinhos via Mercado Pago na tela Créditos.",
        ]}
      />

      {(
        <GlassCard className="p-6 anim-fade-up" data-testid="plans-card">
          <div className="flex items-center gap-3 mb-5">
            <Package size={18} className="text-amber-400" />
            <h3 className="font-display text-white font-semibold text-sm">Planos de revenda</h3>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mb-5">
            {plans.map((p) => (
              <div key={p.id} className="p-4 rounded-xl bg-slate-950/50 border border-slate-800/60 relative" data-testid={`plan-card-${p.id}`}>
                <button data-testid={`plan-delete-${p.id}`} onClick={() => deletePlan(p.id)} className="absolute top-3 right-3 text-slate-600 hover:text-rose-400 transition-colors">
                  <Trash2 size={13} />
                </button>
                <p className="font-display text-white font-semibold text-sm">{p.name}</p>
                <p className="neon-text font-display text-xl font-extrabold mt-1.5">{fmtEUR(p.price_eur ?? p.price_brl)}</p>
                <p className="text-[11px] text-slate-500 mt-1.5">{p.credits_gb} GB · até {p.max_clients} clientes</p>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-5 gap-3">
            <input data-testid="plan-name-input" className="input-dark rounded-xl px-3 py-2.5 text-sm" placeholder="Nome" value={newPlan.name} onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })} />
            <input data-testid="plan-price-input" type="number" className="input-dark rounded-xl px-3 py-2.5 text-sm" placeholder="Preço €" value={newPlan.price_eur} onChange={(e) => setNewPlan({ ...newPlan, price_eur: e.target.value })} />
            <input data-testid="plan-credits-input" type="number" className="input-dark rounded-xl px-3 py-2.5 text-sm" placeholder="GB inclusos" value={newPlan.credits_gb} onChange={(e) => setNewPlan({ ...newPlan, credits_gb: e.target.value })} />
            <input data-testid="plan-clients-input" type="number" className="input-dark rounded-xl px-3 py-2.5 text-sm" placeholder="Máx. clientes" value={newPlan.max_clients} onChange={(e) => setNewPlan({ ...newPlan, max_clients: e.target.value })} />
            <button data-testid="plan-create-btn" onClick={createPlan} disabled={!newPlan.name || !newPlan.price_eur} className="btn-violet rounded-xl px-4 py-2.5 text-sm">Criar plano</button>
          </div>
        </GlassCard>
      )}

      <GlassCard className="p-6 anim-fade-up anim-delay-1" data-testid="clients-card">
        <div className="flex items-center gap-3 mb-5">
          <Users size={18} className="text-cyan-400" />
          <h3 className="font-display text-white font-semibold text-sm">Nova conta de revenda</h3>
        </div>
        <div className="grid sm:grid-cols-5 gap-3 mb-7">
          <input data-testid="client-name-input" className="input-dark rounded-xl px-3 py-2.5 text-sm" placeholder="Nome" value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} />
          <input data-testid="client-email-input" type="email" className="input-dark rounded-xl px-3 py-2.5 text-sm" placeholder="Email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} />
          <input data-testid="client-password-input" type="text" className="input-dark rounded-xl px-3 py-2.5 text-sm" placeholder="Senha" value={newClient.password} onChange={(e) => setNewClient({ ...newClient, password: e.target.value })} />
          <input data-testid="client-credits-input" type="number" className="input-dark rounded-xl px-3 py-2.5 text-sm" placeholder="GB iniciais" value={newClient.credits_gb} onChange={(e) => setNewClient({ ...newClient, credits_gb: e.target.value })} />
          <button data-testid="reseller-add-client-btn" onClick={createClient} disabled={!newClient.name || !newClient.email || !newClient.password} className="btn-neon rounded-xl px-4 py-2.5 text-sm flex items-center justify-center gap-2">
            <Plus size={15} /> Criar conta
          </button>
        </div>

        {users === null ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : users.length === 0 ? (
          <EmptyState icon={Users} title="Nenhum revendedor ainda" subtitle="Crie a primeira conta de revenda acima." testid="clients-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-mono2 uppercase tracking-widest text-slate-500 border-b border-slate-800/60">
                  <th className="px-3 py-3">Nome</th>
                  <th className="px-3 py-3">Email</th>
                  <th className="px-3 py-3">Perfil</th>
                  <th className="px-3 py-3">Saldo</th>
                  <th className="px-3 py-3">Ajustar créditos</th>
                </tr>
              </thead>
              <tbody data-testid="clients-table-body">
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-800/40 last:border-0 hover:bg-white/[0.02] transition-colors" data-testid={`client-row-${u.id}`}>
                    <td className="px-3 py-3.5 text-white font-medium">{u.name}</td>
                    <td className="px-3 py-3.5 text-slate-400">{u.email}</td>
                    <td className="px-3 py-3.5"><span className="text-[10px] font-mono2 uppercase tracking-wider text-cyan-300">{ROLE_LABEL[u.role] || u.role}</span></td>
                    <td className="px-3 py-3.5 text-white font-mono2 text-xs">{fmtGB(u.balance_gb)}</td>
                    <td className="px-3 py-3.5">
                      <div className="flex gap-2 items-center">
                        <input
                          data-testid={`credits-input-${u.id}`}
                          type="number"
                          className="input-dark rounded-lg px-2.5 py-1.5 text-xs w-24"
                          placeholder="± GB"
                          value={creditInputs[u.id] || ""}
                          onChange={(e) => setCreditInputs({ ...creditInputs, [u.id]: e.target.value })}
                        />
                        <button data-testid={`credits-apply-${u.id}`} onClick={() => addCredits(u.id)} className="p-1.5 rounded-lg btn-ghost-dark text-emerald-400" title="Aplicar">
                          <Coins size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>
    </div>
  );
}
